import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Typography, Alert, Result } from 'antd';
import { LockOutlined, SafetyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';

const { Title, Text } = Typography;

const PasswordReset = () => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const [resetSuccess, setResetSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Extract token and email from URL
    const searchParams = new URLSearchParams(location.search);
    const email = searchParams.get('email');
    const token = location.pathname.split('/').pop();

    const validatePassword = (_, value) => {
        if (!value) {
            return Promise.reject('Vui lòng nhập mật khẩu!');
        }

        // Length validation
        if (value.length < 8) {
            return Promise.reject('Mật khẩu phải có ít nhất 8 ký tự!');
        }

        if (value.length > 50) {
            return Promise.reject('Mật khẩu không được vượt quá 50 ký tự!');
        }

        // Password strength validation
        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumbers = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

        // if (!hasUpperCase) {
        //     return Promise.reject('Mật khẩu phải chứa ít nhất 1 chữ in hoa!');
        // }

        // if (!hasLowerCase) {
        //     return Promise.reject('Mật khẩu phải chứa ít nhất 1 chữ thường!');
        // }

        // if (!hasNumbers) {
        //     return Promise.reject('Mật khẩu phải chứa ít nhất 1 số!');
        // }

        // if (!hasSpecialChar) {
        //     return Promise.reject('Mật khẩu phải chứa ít nhất 1 ký tự đặc biệt!');
        // }

        return Promise.resolve();
    };

    const validateConfirmPassword = ({ getFieldValue }) => ({
        validator(_, value) {
            if (!value) {
                return Promise.reject('Vui lòng xác nhận mật khẩu!');
            }
            if (getFieldValue('password') !== value) {
                return Promise.reject('Mật khẩu xác nhận không khớp!');
            }
            return Promise.resolve();
        },
    });

    const handleSubmit = async (values) => {
        if (!email || !token) {
            setErrorMessage('Link đặt lại mật khẩu không hợp lệ!');
            message.error('Link đặt lại mật khẩu không hợp lệ!');
            return;
        }

        try {
            setLoading(true);
            setErrorMessage('');
            const response = await axios.post(`${config.apiUrl}/api/v1/reset-password`, {
                email,
                token,
                password: values.password,
                password_confirmation: values.confirmPassword
            });

            if (response.data.success) {
                setResetSuccess(true);
                message.success('Đặt lại mật khẩu thành công!');
                // Redirect to login page after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setErrorMessage(response.data.message || 'Không thể đặt lại mật khẩu!');
                message.error(response.data.message || 'Không thể đặt lại mật khẩu!');
            }
        } catch (error) {
            console.error('Password reset error:', error);
            const errorMsg = error.response?.data?.message || 
                'Có lỗi xảy ra khi đặt lại mật khẩu. Vui lòng thử lại!';
            setErrorMessage(errorMsg);
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const renderSuccessMessage = () => (
        <Result
            status="success"
            icon={<CheckCircleOutlined />}
            title="Đặt lại mật khẩu thành công!"
            subTitle="Bạn sẽ được chuyển hướng đến trang đăng nhập trong vài giây..."
            extra={[
                <Button 
                    type="primary" 
                    key="login" 
                    onClick={() => navigate('/login')}
                >
                    Đăng nhập ngay
                </Button>
            ]}
        />
    );

    const renderResetForm = () => (
        <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Title level={2}>Đặt lại mật khẩu</Title>
                <Text type="secondary">
                    Nhập mật khẩu mới cho tài khoản {email}
                </Text>
            </div>

            {errorMessage && (
                <Alert
                    message={errorMessage}
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form
                form={form}
                onFinish={handleSubmit}
                layout="vertical"
                requiredMark={false}
            >
                <Form.Item
                    name="password"
                    rules={[{ validator: validatePassword }]}
                    help="Mật khẩu phải chứa chữ hoa, chữ thường, số và ký tự đặc biệt"
                >
                    <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="Mật khẩu mới"
                        size="large"
                    />
                </Form.Item>

                <Form.Item
                    name="confirmPassword"
                    rules={[validateConfirmPassword]}
                >
                    <Input.Password
                        prefix={<SafetyOutlined />}
                        placeholder="Xác nhận mật khẩu mới"
                        size="large"
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                        size="large"
                        style={{ height: '48px' }}
                    >
                        Đặt lại mật khẩu
                    </Button>
                </Form.Item>

                <div style={{ textAlign: 'center' }}>
                    <Button 
                        type="link" 
                        onClick={() => navigate('/login')}
                    >
                        Quay lại đăng nhập
                    </Button>
                </div>
            </Form>
        </>
    );

    return (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            padding: '20px'
        }}>
            <Card style={{ 
                width: '100%', 
                maxWidth: 450,
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                borderRadius: '12px'
            }}>
                {resetSuccess ? renderSuccessMessage() : renderResetForm()}
            </Card>
        </div>
    );
};

export default PasswordReset;